import { Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Reusable editor for JSONB "config bag" columns - partner_config,
 * organization.configuration, location.configuration, location.metadata.
 *
 * Shape matches the admin-api's json_config.shallow_merge_for_patch
 * contract:
 *   - value is always an object (dict)
 *   - top-level keys matching the secret-like regex are rejected
 *     client-side here AND by the API so a split-brain can't
 *     sneak a secret into the bag
 *
 * Two modes:
 *   - "form"  - key/value rows with add/remove, value type chooser
 *   - "raw"   - plain <textarea> with JSON.parse validation
 *
 * The caller controls persistence. This component just produces a
 * `dict` via onChange; the surrounding form wires it to the
 * relevant PATCH endpoint.
 */

// Mirrors tilt_admin_api.json_config._SECRET_KEY_RE. Keys ending in
// key / api_key / secret / token / password / passwd reject - store
// those in /provider-credentials instead.
const SECRET_KEY_RE = /(?:^|_)(api_?key|key|secret|token|password|passwd)$/i;

export type ConfigValue = Record<string, unknown>;

/**
 * A named setting the caller documents for this particular config bag.
 * Rendered as a one-click quick-add chip above the row editor - lets
 * admins pick from known keys instead of guessing what strings are
 * meaningful. Keys already present in `value` are shown as disabled
 * (so clicks don't duplicate rows).
 */
export interface SuggestedKey {
  key: string;
  type: Row["type"];
  /** One-line explanation surfaced as a tooltip on the chip. */
  hint?: string;
  /** Initial value placed into the input when the chip is clicked. */
  defaultInput?: string;
}

export interface JsonConfigEditorProps {
  value: ConfigValue;
  onChange: (v: ConfigValue) => void;
  /** Shown as a caption above the editor (e.g. "Organization configuration"). */
  label?: string;
  /** Hint under the label - e.g. suggested key names for this column. */
  hint?: string;
  /** Known/recommended keys for this bag, rendered as quick-add chips. */
  suggestedKeys?: SuggestedKey[];
  /** Extra classes for the outer container. */
  className?: string;
  /** When true, show a read-only preview; no edits. */
  readOnly?: boolean;
}

type Row = {
  /** Stable per-row id so React reconciliation survives reordering/remove. */
  id: string;
  key: string;
  type: "string" | "number" | "boolean" | "json";
  /** stringified for inputs; parsed on commit */
  input: string;
  error?: string;
};

let _nextRowId = 0;
function newRowId(): string {
  _nextRowId += 1;
  return `r${_nextRowId}`;
}

function isPlainObject(x: unknown): x is ConfigValue {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function inferType(v: unknown): Row["type"] {
  if (typeof v === "string") return "string";
  if (typeof v === "number") return "number";
  if (typeof v === "boolean") return "boolean";
  return "json";
}

function formatForInput(v: unknown, t: Row["type"]): string {
  if (t === "json") return JSON.stringify(v ?? null);
  if (t === "boolean") return v ? "true" : "false";
  return String(v ?? "");
}

function parseInput(raw: string, t: Row["type"]): { value: unknown; error?: string } {
  if (t === "string") return { value: raw };
  if (t === "number") {
    const n = Number(raw);
    if (Number.isNaN(n) || raw.trim() === "") return { value: 0, error: "not a number" };
    return { value: n };
  }
  if (t === "boolean") {
    if (raw === "true") return { value: true };
    if (raw === "false") return { value: false };
    return { value: false, error: "must be true or false" };
  }
  try {
    return { value: JSON.parse(raw) };
  } catch (e) {
    return { value: null, error: e instanceof Error ? e.message : "invalid JSON" };
  }
}

function rowsFromValue(value: ConfigValue): Row[] {
  return Object.entries(value).map(([key, v]) => {
    const t = inferType(v);
    return { id: newRowId(), key, type: t, input: formatForInput(v, t) };
  });
}

function valueFromRows(rows: Row[]): { value: ConfigValue; errors: string[] } {
  const out: ConfigValue = {};
  const errors: string[] = [];
  const seenKeys = new Set<string>();
  for (const r of rows) {
    const key = r.key.trim();
    if (!key) {
      errors.push("empty key");
      continue;
    }
    if (seenKeys.has(key)) {
      errors.push(`duplicate key "${key}"`);
      continue;
    }
    if (SECRET_KEY_RE.test(key)) {
      errors.push(
        `"${key}" looks like a secret - store API keys / tokens / signing secrets in the Credentials page instead`,
      );
      continue;
    }
    const { value, error } = parseInput(r.input, r.type);
    if (error) {
      errors.push(`${key}: ${error}`);
      continue;
    }
    seenKeys.add(key);
    out[key] = value;
  }
  return { value: out, errors };
}

export function JsonConfigEditor(props: JsonConfigEditorProps): JSX.Element {
  const { value, onChange, label, hint, suggestedKeys, className, readOnly } = props;
  const [mode, setMode] = useState<"form" | "raw">("form");
  const [rawText, setRawText] = useState<string>(() => JSON.stringify(value, null, 2));
  const [rawError, setRawError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>(() => rowsFromValue(value));
  const { errors: formErrors } = useMemo(() => valueFromRows(rows), [rows]);
  // Track the last value we emitted via onChange so we don't
  // ping-pong: parent sets value → we emit → parent re-renders →
  // effect sees new value → would reinitialise and wipe edits.
  const lastEmittedRef = useRef<string>(JSON.stringify(value));

  // Reconcile when the parent's `value` changes (refetch after save,
  // switching entities). Skip reconciliation when the incoming value
  // is exactly what we just emitted - that's our own change coming
  // back and rebuilding rows would clobber in-progress keystrokes.
  useEffect(() => {
    const serialised = JSON.stringify(value);
    if (serialised === lastEmittedRef.current) return;
    lastEmittedRef.current = serialised;
    setRows(rowsFromValue(value));
    setRawText(JSON.stringify(value, null, 2));
    setRawError(null);
  }, [value]);

  const commit = (next: ConfigValue): void => {
    lastEmittedRef.current = JSON.stringify(next);
    onChange(next);
  };

  const commitRows = (next: Row[]): void => {
    setRows(next);
    const { value: v, errors } = valueFromRows(next);
    if (errors.length === 0) commit(v);
  };

  const switchToRaw = (): void => {
    setRawText(JSON.stringify(value, null, 2));
    setRawError(null);
    setMode("raw");
  };

  const switchToForm = (): void => {
    // If the textarea is dirty, commit before switching so the form
    // reflects the latest state.
    if (rawError === null) {
      try {
        const parsed = JSON.parse(rawText);
        if (isPlainObject(parsed)) {
          commit(parsed);
          setRows(rowsFromValue(parsed));
        }
      } catch {
        // rawError would already be set via the onChange; fall through.
      }
    }
    setMode("form");
  };

  function firstSecretKey(obj: ConfigValue): string | null {
    for (const k of Object.keys(obj)) if (SECRET_KEY_RE.test(k)) return k;
    return null;
  }

  return (
    <div className={cn("space-y-2", className)}>
      {(label || !readOnly) && (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            {label && <div className="text-xs font-medium text-muted-foreground">{label}</div>}
            {hint && <div className="text-[11px] text-muted-foreground/80">{hint}</div>}
          </div>
          {!readOnly && (
            <div className="flex gap-1 text-xs">
              <button
                type="button"
                onClick={mode === "raw" ? switchToForm : undefined}
                className={cn(
                  "rounded-md border px-2 py-0.5",
                  mode === "form"
                    ? "bg-primary/10 text-primary"
                    : "bg-background text-muted-foreground hover:bg-muted",
                )}
              >
                Form
              </button>
              <button
                type="button"
                onClick={mode === "form" ? switchToRaw : undefined}
                className={cn(
                  "rounded-md border px-2 py-0.5",
                  mode === "raw"
                    ? "bg-primary/10 text-primary"
                    : "bg-background text-muted-foreground hover:bg-muted",
                )}
              >
                Raw JSON
              </button>
            </div>
          )}
        </div>
      )}

      {mode === "raw" ? (
        <div className="space-y-1">
          <textarea
            rows={10}
            readOnly={readOnly}
            value={rawText}
            onChange={(e) => {
              const next = e.target.value;
              setRawText(next);
              try {
                const parsed = JSON.parse(next);
                if (!isPlainObject(parsed)) {
                  setRawError("must be a JSON object");
                  return;
                }
                // Raw-JSON callers get the same secret-key guard the
                // form UI enforces - a string literal of "api_key" in
                // the textarea must not slip past the client and only
                // surface at the API boundary.
                const bad = firstSecretKey(parsed);
                if (bad) {
                  setRawError(
                    `"${bad}" looks like a secret - store API keys / tokens / signing secrets in the Credentials page instead`,
                  );
                  return;
                }
                setRawError(null);
                commit(parsed);
              } catch (err) {
                setRawError(err instanceof Error ? err.message : "invalid JSON");
              }
            }}
            className={cn(
              "w-full rounded-md border bg-background px-3 py-2 font-mono text-xs",
              rawError && "border-destructive",
            )}
            aria-invalid={rawError !== null}
          />
          {rawError && <p className="text-[11px] text-destructive">{rawError}</p>}
        </div>
      ) : (
        <div className="space-y-1.5">
          {!readOnly && suggestedKeys && suggestedKeys.length > 0 && (
            <div className="rounded-md border bg-muted/30 p-2">
              <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">
                Common settings - click to add
              </p>
              <div className="flex flex-wrap gap-1.5">
                {suggestedKeys.map((s) => {
                  const taken = rows.some((r) => r.key.trim() === s.key);
                  return (
                    <button
                      key={s.key}
                      type="button"
                      disabled={taken}
                      title={s.hint ?? s.key}
                      onClick={() =>
                        setRows([
                          ...rows,
                          {
                            id: newRowId(),
                            key: s.key,
                            type: s.type,
                            input: s.defaultInput ?? (s.type === "boolean" ? "false" : ""),
                          },
                        ])
                      }
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
                        taken
                          ? "cursor-not-allowed border-muted bg-muted/50 text-muted-foreground/60 line-through"
                          : "border-primary/30 bg-background text-primary hover:bg-primary/10",
                      )}
                    >
                      <Plus className="h-2.5 w-2.5" aria-hidden="true" />
                      {s.key}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {rows.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {suggestedKeys && suggestedKeys.length > 0
                ? "No settings yet. Click a chip above to add a common one, or use “Add setting” below for a custom key."
                : "No settings yet. Add a key/value pair below."}
            </p>
          )}
          {rows.map((r, i) => (
            <KeyValueRow
              key={r.id}
              row={r}
              readOnly={!!readOnly}
              onChange={(next) => {
                const copy = [...rows];
                copy[i] = next;
                commitRows(copy);
              }}
              onRemove={() => commitRows(rows.filter((_, j) => j !== i))}
            />
          ))}
          {formErrors.length > 0 && (
            <ul className="rounded-md bg-destructive/10 p-2 text-[11px] text-destructive">
              {formErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={() =>
                setRows([...rows, { id: newRowId(), key: "", type: "string", input: "" }])
              }
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              Add setting
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function KeyValueRow(props: {
  row: Row;
  readOnly: boolean;
  onChange: (next: Row) => void;
  onRemove: () => void;
}): JSX.Element {
  const { row, readOnly, onChange, onRemove } = props;
  const [showLong, setShowLong] = useState(false);
  // Match valueFromRows - it trims before validating, so the UI feedback
  // has to trim too or a trailing-space key would look clean but be
  // rejected on commit.
  const trimmedKey = row.key.trim();
  const keyError = trimmedKey && SECRET_KEY_RE.test(trimmedKey);
  const isLong = row.type === "json" || row.input.length > 60;
  return (
    <div className="flex flex-wrap items-start gap-2">
      <input
        type="text"
        value={row.key}
        onChange={(e) => onChange({ ...row, key: e.target.value })}
        placeholder="key"
        disabled={readOnly}
        aria-label="Setting key"
        aria-invalid={!!keyError}
        className={cn(
          "w-48 rounded-md border bg-background px-2 py-1 text-xs font-mono",
          keyError && "border-destructive",
        )}
      />
      <select
        value={row.type}
        onChange={(e) => {
          const t = e.target.value as Row["type"];
          onChange({ ...row, type: t, input: formatForInput(null, t) });
        }}
        disabled={readOnly}
        aria-label="Value type"
        className="rounded-md border bg-background px-2 py-1 text-xs"
      >
        <option value="string">string</option>
        <option value="number">number</option>
        <option value="boolean">boolean</option>
        <option value="json">JSON</option>
      </select>
      {row.type === "boolean" ? (
        <select
          value={row.input}
          onChange={(e) => onChange({ ...row, input: e.target.value })}
          disabled={readOnly}
          aria-label="Value"
          className="w-28 rounded-md border bg-background px-2 py-1 text-xs"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : isLong && !showLong ? (
        <button
          type="button"
          onClick={() => setShowLong(true)}
          className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
        >
          <Eye className="h-3 w-3" aria-hidden="true" />
          show ({row.input.length} chars)
        </button>
      ) : (
        <>
          {row.type === "json" ? (
            <textarea
              value={row.input}
              onChange={(e) => onChange({ ...row, input: e.target.value })}
              disabled={readOnly}
              aria-label="Value"
              rows={Math.min(8, Math.max(2, row.input.split("\n").length))}
              className="min-w-[16rem] flex-1 rounded-md border bg-background px-2 py-1 font-mono text-xs"
            />
          ) : (
            <input
              type={row.type === "number" ? "number" : "text"}
              value={row.input}
              onChange={(e) => onChange({ ...row, input: e.target.value })}
              disabled={readOnly}
              aria-label="Value"
              className="min-w-[16rem] flex-1 rounded-md border bg-background px-2 py-1 text-xs"
            />
          )}
          {showLong && (
            <button
              type="button"
              onClick={() => setShowLong(false)}
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            >
              <EyeOff className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove setting"
          title="Remove setting"
          className="flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
